import './../MultiRangeSlider.scss';

import MultiRangeSlider from 'multi-range-slider-react';
import * as React from 'react';
import { useRef, useState } from 'react';

import { translate } from '~l10n/client';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import { debounce } from 'underscore';

import { AlertMessage } from '~shared/components/AlertMessage';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { useAutoFocus } from '~shared/components/helpers';
import { Spinner } from '~shared/components/Spinner';
import TextInput from '~shared/components/TextInput';
import { useEmbedVideoModal } from '~teacher/components/QuizQuestion/EmbedVideoDialog/context/EmbedVideoDialogProvider';
import { ensureHttps } from '~teacher/components/QuizQuestion/EmbedVideoDialog/utils/ensureHttps';
import { generateRange } from '~teacher/components/QuizQuestion/EmbedVideoDialog/utils/generateRange';
import { hasMultipleUrls } from '~teacher/components/QuizQuestion/EmbedVideoDialog/utils/hasMultipleUrls';
import { isValidVimeoLink } from '~teacher/components/QuizQuestion/EmbedVideoDialog/utils/isValidVimeoLink';
import { isValidYoutubeLink } from '~teacher/components/QuizQuestion/EmbedVideoDialog/utils/isValidYoutubeLink';
import { toHHMMSS } from '~teacher/components/QuizQuestion/EmbedVideoDialog/utils/toHHMMSS';
import { selectEditQuestion } from '~teacher/slices/quiz/quizSelectors';

import platform from '../../../../../../../shared/Platform';

// Error message/code and a boolean value to determine whether to display the error and validation message or not.
const ignoreErrorsMap = new Map<string, boolean>([
  // The Vimeo player intermittently throws the following errors, but the video plays successfully.
  ['PlaybackError', platform.isAndroidApp],
  ["Cannot read properties of undefined (reading 'indexOf')", false],
  /* Error codes for YouTube: Video playback disabled by the owner.
   Reference: https://developers.google.com/youtube/iframe_api_reference#:~:text=onError,error%20in%20disguise */
  ['101', true],
  ['150', true],
]);

export enum UrlValidation {
  Empty = 'empty',
  Valid = 'valid',
  Invalid = 'invalid',
  NotEmbeddable = 'notEmbeddable',
}

export function EmbedVideoDialog() {
  const embedVideoModal = useEmbedVideoModal();
  return (
    <Dialog
      open={embedVideoModal.isOpen}
      onClose={embedVideoModal.close}
      size="large"
      id={'embed-video-dialog'}
      title={translate('Embed Video')}
    >
      {embedVideoModal.isOpen ? <EmbedVideo /> : <Spinner />}
    </Dialog>
  );
}

const debouncedSetVideoUrl = debounce(
  ({
    url,
    setUrl,
    setUrlValidation,
  }: {
    url: string;
    setUrl: (url: string) => void;
    setUrlValidation: (urlValidation: UrlValidation) => void;
  }) => {
    setUrl(url);
    const urlValidation = checkIsEmbeddableUrl(url);
    setUrlValidation(urlValidation);
  },
  500
);

function EmbedVideo() {
  const existingVideoData = useSelector(selectEditQuestion);
  const initialUrl = existingVideoData?.questionVideo?.url ?? '';
  const [inputUrl, setInputUrl] = useState<string | null>(initialUrl);
  const [videoUrl, setVideoUrl] = useState<string | null>(initialUrl);
  const [urlValidation, setUrlValidation] = useState<UrlValidation>(
    initialUrl ? UrlValidation.Valid : UrlValidation.Empty
  );
  const [minValue, setMinValue] = useState(
    existingVideoData?.questionVideo?.start ?? 0
  );
  const [maxValue, setMaxValue] = useState(
    existingVideoData?.questionVideo?.end ?? 0
  );
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const reactPlayerRef = useRef<ReactPlayer>(null);
  const autofocus = useAutoFocus(true);
  const embedVideoModal = useEmbedVideoModal();

  // This will create equally spaced range of values between 0 and the duration of the video
  const range = generateRange({
    upper: reactPlayerRef.current?.getDuration() ?? 0,
    lower: 0,
    steps: 7,
  });

  // This will convert the range of values to HH:MM:SS format
  const labels = range.map(toHHMMSS);

  const handleMinValueChange = (newMinValue: number) => {
    setMinValue(newMinValue);
  };

  const handleMaxValueChange = (newMaxValue: number) => {
    setMaxValue(newMaxValue);
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value?.trim();

    if (newUrl === inputUrl) return;

    setIsVideoLoaded(false);
    setInputUrl(newUrl);
    if (newUrl !== videoUrl) {
      // Reset the min and max values if the url changes
      setMinValue(0);
      setMaxValue(0);
    }
    debouncedSetVideoUrl({
      url: newUrl,
      setUrl: setVideoUrl,
      setUrlValidation,
    });

    if (newUrl.length === 0) {
      setUrlValidation(UrlValidation.Empty);
      return;
    }
  };

  const handleErrors = (e: Error) => {
    const errorMessages = typeof e === 'string' ? e : e.message;

    if (ignoreErrorsMap.has(errorMessages)) {
      if (ignoreErrorsMap.get(errorMessages)) {
        setUrlValidation(UrlValidation.NotEmbeddable);
      } else {
        setUrlValidation(UrlValidation.Valid);
      }
      return;
    }

    setUrlValidation(UrlValidation.Invalid);
  };

  return (
    <div style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          // If video max value if not greater than 0, then it means the video has not loaded
          const hasValidMaxValue =
            range?.length > 0 && range[range.length - 1] > 0;

          if (!hasValidMaxValue) {
            setUrlValidation(UrlValidation.Invalid);
            return;
          }

          if (urlValidation !== UrlValidation.Valid) return;

          embedVideoModal.onSubmit({
            url: ensureHttps(videoUrl),
            start: minValue ? minValue : 0,
            end: maxValue ? maxValue : reactPlayerRef.current?.getDuration(),
            duration: reactPlayerRef.current?.getDuration(),
          });
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 pt-5">
            <label
              htmlFor="video-url"
              className="font-sans text-base leading-8 text-blue-900"
            >
              {translate('Video URL')}
            </label>
            <TextInput
              id="video-url"
              aria-required="true"
              aria-describedby="video-url-description"
              placeholder={translate('Paste the video URL…')}
              ref={autofocus}
              value={inputUrl}
              onChange={handleUrlChange}
            />
            <div
              id="video-url-description"
              className="font-sans text-xs leading-8 text-blue-600"
            >
              {translate('Embed a video from Youtube or Vimeo.')}
            </div>
          </div>
          {[UrlValidation.Valid, UrlValidation.NotEmbeddable].includes(
            urlValidation
          ) ? (
            <div className="flex flex-col flex-grow flex-shrink-0 gap-5 pb-9">
              {/* Without an explicit height react-player seems to pick whatever height it wants */}
              <ReactPlayer
                width="100%"
                playsinline={platform.isWrapperApp()}
                ref={reactPlayerRef}
                playing={isPlaying}
                onPlay={() => setIsPlaying(true)}
                onProgress={({ playedSeconds }) => {
                  if (playedSeconds > maxValue) {
                    setIsPlaying(false);
                    reactPlayerRef.current?.seekTo(maxValue);
                  }

                  if (playedSeconds < minValue) {
                    setIsPlaying(false);
                    reactPlayerRef.current?.seekTo(minValue);
                  }
                }}
                config={{
                  youtube: { playerVars: { start: minValue, end: maxValue } },
                  vimeo: { playerOptions: { minValue, maxValue, dnt: true } },
                }}
                url={ensureHttps(videoUrl)}
                controls={true}
                onDuration={(duration) => {
                  if (!duration || duration < 1) {
                    // If the video has no duration, it means it has failed to load
                    setUrlValidation(UrlValidation.Invalid);
                    return;
                  }
                  setMaxValue((prev) => (prev && prev > 0 ? prev : duration));
                  setIsVideoLoaded(true);
                }}
                className="max-h-100 rounded-2 shadow-outline-px inset-0 text-grey-400 overflow-hidden"
                onError={handleErrors}
              />

              {isVideoLoaded && urlValidation === UrlValidation.Valid ? (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="video-url"
                    className="font-sans text-base leading-8 text-blue-900"
                  >
                    {translate('Start and End Time')}
                  </label>
                  <MultiRangeSlider
                    baseClassName="multi-range-slider-custom"
                    key={videoUrl}
                    min={0}
                    max={reactPlayerRef.current?.getDuration() ?? 0}
                    step={1}
                    subSteps={true}
                    minValue={minValue}
                    maxValue={maxValue}
                    labels={labels}
                    canMinMaxValueSame={false}
                    onInput={({
                      minValue: newMinValue,
                      maxValue: newMaxValue,
                    }) => {
                      if (minValue !== newMinValue) {
                        reactPlayerRef.current?.seekTo(newMinValue);
                      }
                      if (maxValue !== newMaxValue) {
                        reactPlayerRef.current?.seekTo(newMaxValue);
                      }
                      handleMinValueChange(newMinValue);
                      handleMaxValueChange(newMaxValue);
                    }}
                    ruler={true}
                    maxCaption={toHHMMSS(maxValue)}
                    minCaption={toHHMMSS(minValue)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div role="region" aria-live="polite" className="pb-2">
          {[UrlValidation.Invalid, UrlValidation.NotEmbeddable].includes(
            urlValidation
          ) ? (
            <AlertMessage tone="critical" showIcon>
              {translate(
                'Invalid or unsupported URL. Please check your URL and try again.'
              )}
            </AlertMessage>
          ) : null}
        </div>
        <div
          className="flex flex-row justify-end gap-2 pt-8"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <SmallButton onClick={embedVideoModal.close}>
            {translate('Cancel')}
          </SmallButton>
          <SmallButton
            isDisabled={urlValidation !== UrlValidation.Valid || !isVideoLoaded}
            type="submit"
          >
            {translate('Embed Video')}
          </SmallButton>
        </div>
      </form>
    </div>
  );
}

/**
 * Checks if a url is embeddable in our react player
 * @param url
 */
export function checkIsEmbeddableUrl(url: string) {
  if (url.length <= 0) return UrlValidation.Empty;

  if (hasMultipleUrls(url)) return UrlValidation.Invalid;

  // Should match at least one but not both
  if (isValidYoutubeLink(url) && isValidVimeoLink(url))
    return UrlValidation.Invalid;
  if (isValidYoutubeLink(url) || isValidVimeoLink(url))
    return UrlValidation.Valid;

  // If it does not match anything. It fails!
  return UrlValidation.Invalid;
}
